/**
 * FHIR API Service
 * Handles all FHIR-related API calls
 */

import {
  FHIRApiResponse,
  FHIRBundle,
  FHIRBundleResponse,
  FHIRExportOptions,
  FHIRImportResult,
  FHIRPatient,
  FHIRResourceType,
} from "@/types/fhir";
import { apiHelpers } from "../api";

export const fhirApi = {
  /**
   * Get FHIR Patient resource
   */
  getPatient: async (patientId: string): Promise<FHIRPatient> => {
    const response = await apiHelpers.get<FHIRPatient>(
      `/fhir/Patient/${patientId}`,
    );
    return response.data;
  },

  /**
   * Get all Observations for authenticated user
   */
  getObservations: async (): Promise<FHIRBundle> => {
    const response = await apiHelpers.get<FHIRBundle>("/fhir/Observation");
    return response.data;
  },

  /**
   * Get all AllergyIntolerance resources for authenticated user
   */
  getAllergies: async (): Promise<FHIRBundle> => {
    const response = await apiHelpers.get<FHIRBundle>(
      "/fhir/AllergyIntolerance",
    );
    return response.data;
  },

  /**
   * Get all Condition resources for authenticated user
   */
  getConditions: async (): Promise<FHIRBundle> => {
    const response = await apiHelpers.get<FHIRBundle>("/fhir/Condition");
    return response.data;
  },

  /**
   * Get all Immunization resources for authenticated user
   */
  getImmunizations: async (): Promise<FHIRBundle> => {
    const response = await apiHelpers.get<FHIRBundle>("/fhir/Immunization");
    return response.data;
  },

  /**
   * Get complete FHIR Bundle for user
   */
  getBundle: async (userId: string): Promise<FHIRBundle> => {
    const response = await apiHelpers.get<FHIRBundle>(`/fhir/Bundle/${userId}`);
    return response.data;
  },

  /**
   * Generate and store complete FHIR Bundle
   */
  generateBundle: async (): Promise<FHIRBundleResponse> => {
    const response =
      await apiHelpers.post<FHIRBundleResponse>("/fhir/generate");
    return response.data;
  },

  /**
   * Get FHIR resources by type
   */
  getResourcesByType: async (
    resourceType?: FHIRResourceType,
  ): Promise<FHIRBundle> => {
    const params = resourceType ? { resourceType } : {};
    const response = await apiHelpers.get<FHIRBundle>("/fhir/resources", {
      params,
    });
    return response.data;
  },

  /**
   * Get FHIR CapabilityStatement
   */
  getCapabilityStatement: async (): Promise<unknown> => {
    const response = await apiHelpers.get<unknown>("/fhir/metadata");
    return response.data;
  },

  /**
   * Health check for FHIR service
   */
  healthCheck: async (): Promise<{
    status: string;
    service: string;
    fhirVersion: string;
  }> => {
    const response = await apiHelpers.get<{
      status: string;
      service: string;
      fhirVersion: string;
    }>("/fhir/health");
    return response.data;
  },

  /**
   * Export FHIR data
   */
  exportData: async (options: FHIRExportOptions): Promise<Blob> => {
    const response = await apiHelpers.post<Blob>("/fhir/export", options, {
      responseType: "blob",
      headers: {
        Accept:
          options.format === "xml"
            ? "application/fhir+xml"
            : "application/fhir+json",
      },
    });
    return response.data;
  },

  /**
   * Import FHIR data
   */
  importData: async (file: File): Promise<FHIRImportResult> => {
    const formData = new FormData();
    formData.append("fhir-file", file);

    const response = await apiHelpers.post<FHIRImportResult>(
      "/fhir/import",
      formData,
    );
    return response.data;
  },

  /**
   * Validate FHIR resource
   */
  validateResource: async (
    resource: Record<string, unknown>,
  ): Promise<FHIRApiResponse> => {
    const response = await apiHelpers.post<FHIRApiResponse>(
      "/fhir/validate",
      resource,
    );
    return response.data;
  },
};

export default fhirApi;
